/**
 * Feedback service — orchestrates environment data collection and submits
 * feedback (bug reports / feature requests) to the Azure Function API.
 */

declare const __APP_VERSION__: string;

import * as Sentry from '@sentry/react';
import { getRecentErrors } from '../utils/consoleCapture';
import { captureViewport, captureCanvas } from './screenshotService';
import { apiFetch } from './apiClient';

// ---------------------------------------------------------------------------
// Exported interfaces
// ---------------------------------------------------------------------------

export interface FeedbackEnvironment {
  appVersion: string;
  route: string;
  projectName?: string;
  projectId?: string;
  browser: string;
  os: string;
  screenSize: string;
  deviceType: string;
  timestamp: string;
  // Survey context (when on survey page)
  floorName?: string;
  floorCount?: number;
  deviceCount?: number;
  activeTab?: string;
}

export interface FeedbackUserInput {
  title: string;
  description: string;
  stepsToReproduce?: string;
  expectedBehavior?: string;
  priority?: 'low' | 'medium' | 'high';
}

export interface FeedbackReporter {
  name: string;
  email: string;
}

export interface FeedbackScreenshots {
  viewport: string | null;
  canvas: string | null;
}

/** Structural match for Sentry's `Attachment` — kept Sentry-free for tests. */
export interface ScreenshotAttachment {
  filename: string;
  contentType: 'image/jpeg';
  data: Uint8Array;
}

/**
 * Convert the wizard's captured screenshots (bare base64 JPEG strings, see
 * screenshotService) into Sentry event attachments. Standalone User Feedback
 * items can't carry images, so the submit path attaches these to a carrier
 * event and links the feedback to it — otherwise the captured screenshots only
 * reach GHE, and while GHE is down they were discarded entirely.
 */
export function screenshotsToAttachments(screenshots: FeedbackScreenshots): ScreenshotAttachment[] {
  const entries: Array<{ filename: string; base64: string | null }> = [
    { filename: 'feedback-viewport.jpg', base64: screenshots.viewport },
    { filename: 'feedback-canvas.jpg', base64: screenshots.canvas },
  ];
  const attachments: ScreenshotAttachment[] = [];
  for (const { filename, base64 } of entries) {
    if (!base64) continue;
    try {
      const binary = atob(base64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      attachments.push({ filename, contentType: 'image/jpeg', data: bytes });
    } catch {
      // Skip malformed captures — never fail the feedback over an attachment.
    }
  }
  return attachments;
}

export interface FeedbackSubmitResult {
  issueUrl: string;
  issueNumber: number;
  screenshotWarning?: string;
  /**
   * True when the report was accepted by Sentry (the system of record) but the
   * best-effort GHE mirror issue could not be created. Submission still
   * succeeded — only the issue-tracker copy is pending.
   */
  gheMirrorFailed?: boolean;
}

export interface SurveyContext {
  projectName?: string;
  floorName?: string;
  floorCount?: number;
  deviceCount?: number;
  activeTab?: string;
}

export interface WizardState {
  type: 'bug' | 'feature';
  // Bug steps
  whatDoing?: string;
  whatHappened?: string;
  whatExpected?: string;
  severity?: 'critical' | 'medium' | 'low';
  // Feature steps
  goal?: string;
  currentWorkaround?: string;
  desiredImprovement?: string;
  // Editable title (review step)
  customTitle?: string;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** Parses navigator.userAgent to extract browser name+version and OS name. */
function parseBrowser(): { browser: string; os: string } {
  const ua = navigator.userAgent;

  // --- OS detection ---
  let os: string;
  if (/iPad/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)) {
    os = 'iPadOS';
  } else if (/iPhone|iPod/.test(ua)) {
    os = 'iOS';
  } else if (/Macintosh/.test(ua)) {
    os = 'macOS';
  } else if (/Windows/.test(ua)) {
    os = 'Windows';
  } else if (/Android/.test(ua)) {
    os = 'Android';
  } else if (/Linux/.test(ua)) {
    os = 'Linux';
  } else {
    os = 'Unknown';
  }

  // --- Browser detection (order matters — most specific first) ---
  let browser: string;

  // Edge (Chromium-based) — identifies itself as both Chrome and Edg
  const edgeMatch = ua.match(/Edg\/(\d+\.\d+)/);
  if (edgeMatch) {
    browser = `Edge ${edgeMatch[1]}`;
  }
  // Chrome on iOS (uses CriOS token)
  else {
    const chromeIosMatch = ua.match(/CriOS\/(\d+\.\d+)/);
    if (chromeIosMatch) {
      browser = `Chrome iOS ${chromeIosMatch[1]}`;
    }
    // Desktop/Android Chrome
    else {
      const chromeMatch = ua.match(/Chrome\/(\d+\.\d+)/);
      if (chromeMatch) {
        browser = `Chrome ${chromeMatch[1]}`;
      }
      // Firefox
      else {
        const firefoxMatch = ua.match(/Firefox\/(\d+\.\d+)/);
        if (firefoxMatch) {
          browser = `Firefox ${firefoxMatch[1]}`;
        }
        // Safari (must come after Chrome/Edge checks since they include "Safari" token too)
        else {
          const safariMatch = ua.match(/Version\/(\d+\.\d+).*Safari/);
          if (safariMatch) {
            browser = `Safari ${safariMatch[1]}`;
          } else {
            browser = 'Unknown';
          }
        }
      }
    }
  }

  return { browser, os };
}

/** Returns a human-readable device type string. */
function getDeviceType(): string {
  const hasTouch = navigator.maxTouchPoints > 0;
  const ua = navigator.userAgent;

  const isIPad =
    /iPad/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  const isMobile = /iPhone|iPod|Android/.test(ua);

  if (isIPad) return 'iPad (touch)';
  if (isMobile) return 'Mobile (touch)';
  if (hasTouch) return 'Desktop (touch)';
  return 'Desktop';
}

// ---------------------------------------------------------------------------
// Exported functions
// ---------------------------------------------------------------------------

/**
 * Collects environment metadata for inclusion in a feedback submission.
 */
export function collectEnvironment(
  route: string,
  projectName?: string,
  projectId?: string,
  surveyContext?: SurveyContext,
): FeedbackEnvironment {
  const { browser, os } = parseBrowser();

  return {
    appVersion: __APP_VERSION__,
    route,
    ...(projectName !== undefined && { projectName }),
    ...(projectId !== undefined && { projectId }),
    browser,
    os,
    screenSize: `${window.innerWidth}x${window.innerHeight}`,
    deviceType: getDeviceType(),
    timestamp: new Date().toISOString(),
    // Spread survey context fields (only defined ones)
    ...(surveyContext?.floorName !== undefined && { floorName: surveyContext.floorName }),
    ...(surveyContext?.floorCount !== undefined && { floorCount: surveyContext.floorCount }),
    ...(surveyContext?.deviceCount !== undefined && { deviceCount: surveyContext.deviceCount }),
    ...(surveyContext?.activeTab !== undefined && { activeTab: surveyContext.activeTab }),
  };
}

/**
 * Captures viewport and canvas screenshots in parallel.
 */
export async function captureScreenshots(): Promise<FeedbackScreenshots> {
  const [viewport, canvas] = await Promise.all([
    captureViewport(),
    Promise.resolve(captureCanvas()),
  ]);
  return { viewport, canvas };
}

/**
 * Submits feedback. Sentry User Feedback is the primary sink ("Sentry for
 * everything", 2026-09-23); the GHE issue is a best-effort mirror for issue
 * tracking. A GHE failure therefore never blocks a submission that Sentry
 * accepted — it surfaces as `gheMirrorFailed` instead of throwing. Only when
 * Sentry is unavailable (e.g. a dev build with no DSN) does a GHE failure
 * throw, preserving the old fail-loud behavior where there is no other sink.
 */
export async function submitFeedback(
  type: 'bug' | 'feature',
  userInput: FeedbackUserInput,
  environment: FeedbackEnvironment,
  reporter: FeedbackReporter,
  screenshots: FeedbackScreenshots,
): Promise<FeedbackSubmitResult> {
  // 1. Sentry first — the system of record for all feedback. Awaited so a
  // rejection is known before deciding whether GHE failure can be tolerated.
  // The screenshots ride a carrier event the feedback links back to via
  // associatedEventId (standalone feedback can't carry attachments), and
  // includeReplay attaches the in-flight session replay. Attachments go on the
  // scope (the captureContext union has no attachments slot) and are cleared
  // with the scope after capture. withScope only wraps the synchronous part,
  // so the sendFeedback promise escapes via closure and is awaited below.
  let sentrySend: Promise<unknown> | undefined;
  try {
    Sentry.withScope((scope) => {
      const feedbackKind = type === 'bug' ? 'bug' : 'feature';
      const attachments = screenshotsToAttachments(screenshots);
      for (const attachment of attachments) {
        scope.addAttachment(attachment);
      }
      const carrierEventId = attachments.length > 0
        ? Sentry.captureMessage(`[${feedbackKind === 'bug' ? 'bug-report' : 'feature-request'}] ${userInput.title}`, {
            level: 'info',
            tags: { app: 'hts-sitesurvey', feedback_type: feedbackKind, origin: 'feedback-wizard' },
          })
        : undefined;
      sentrySend = Sentry.sendFeedback({
        message: `${userInput.title}\n\n${userInput.description}`,
        name: reporter.name,
        email: reporter.email,
        url: window.location.pathname,
        source: feedbackKind === 'bug' ? 'bug-report' : 'feature-request',
        tags: { app: 'hts-sitesurvey', feedback_type: feedbackKind, origin: 'feedback-wizard' },
        ...(carrierEventId ? { associatedEventId: carrierEventId } : {}),
      }, { includeReplay: true });
    });
  } catch {
    // Sentry not configured (no client) — sentrySend stays undefined.
  }

  let sentryAccepted = false;
  if (sentrySend) {
    try {
      await sentrySend;
      sentryAccepted = true;
    } catch {
      // Sentry rejected/errored — treat as unavailable; GHE below is the only
      // sink, so its failure must throw.
    }
  }

  // 2. GHE issue — best-effort mirror. Failure is non-fatal once Sentry has
  // the report (the dead SURVEY_GHE_PAT on 2026-09-23 must never again make a
  // user unable to submit).
  const body = {
    type,
    ...userInput,
    environment,
    reporter,
    screenshots,
    consoleErrors: getRecentErrors(),
  };

  try {
    const response = await apiFetch(`/ghe/create-issue`, {
      method: 'POST',
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      let message = `Request failed with status ${response.status}`;
      try {
        const errorData = (await response.json()) as {
          message?: string;
          error?: string;
          details?: string;
        };
        message = errorData.message ?? errorData.error ?? message;
        // Include details if available (e.g., credentials not configured)
        if (errorData.details) {
          message = `${message}. ${errorData.details}`;
        }
      } catch {
        // ignore JSON parse failure — use the default message
      }
      throw new Error(message);
    }

    return (await response.json()) as FeedbackSubmitResult;
  } catch (err) {
    if (!sentryAccepted) throw err;
    // The submission is safe in Sentry; this is a mirror outage. Capture it so
    // the dead-token class of failure (SURVEY_GHE_PAT, 2026-09-23) pages the
    // team instead of silently skipping GHE copies. Alert rule fires on the
    // feedback_mirror=failed tag.
    try {
      Sentry.captureMessage('Feedback GHE mirror failed (submission saved in Sentry)', {
        level: 'warning',
        tags: {
          app: 'hts-sitesurvey',
          feedback_type: type,
          source: type === 'bug' ? 'bug-report' : 'feature-request',
          feedback_mirror: 'failed',
          origin: 'feedback-wizard',
        },
        extra: { error: err instanceof Error ? err.message : String(err) },
      });
    } catch {
      // Sentry unconfigured (dev build) — nothing more to do.
    }
    return { issueUrl: '', issueNumber: 0, gheMirrorFailed: true };
  }
}

/**
 * Maps wizard state to the existing API payload shape.
 * No API changes needed — the wizard just provides richer content.
 */
export function buildWizardPayload(
  state: WizardState,
  environment: FeedbackEnvironment,
  reporter: FeedbackReporter,
  screenshots: FeedbackScreenshots,
): {
  type: 'bug' | 'feature';
  userInput: FeedbackUserInput;
  environment: FeedbackEnvironment;
  reporter: FeedbackReporter;
  screenshots: FeedbackScreenshots;
} {
  const severityToPriority: Record<string, 'low' | 'medium' | 'high'> = {
    critical: 'high',
    medium: 'medium',
    low: 'low',
  };

  if (state.type === 'bug') {
    const autoTitle = (state.whatHappened || '').substring(0, 100);
    return {
      type: 'bug',
      userInput: {
        title: state.customTitle || autoTitle,
        description: state.whatHappened || '',
        stepsToReproduce: state.whatDoing,
        expectedBehavior: state.whatExpected,
        priority: state.severity ? severityToPriority[state.severity] : undefined,
      },
      environment,
      reporter,
      screenshots,
    };
  }

  // Feature request: concatenate three answers into description
  const description = [
    `**Goal:** ${state.goal || ''}`,
    '',
    `**Current approach:** ${state.currentWorkaround || 'N/A'}`,
    '',
    `**Desired improvement:** ${state.desiredImprovement || ''}`,
  ].join('\n');

  const autoTitle = (state.goal || '').substring(0, 100);

  return {
    type: 'feature',
    userInput: {
      title: state.customTitle || autoTitle,
      description,
    },
    environment,
    reporter,
    screenshots,
  };
}
